// Lib
import React from 'react';
import Handsontable from 'handsontable/base';
import PropTypes from 'prop-types';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { registerEditor } from 'handsontable/editors';
import {
    AutoColumnSize,
    Autofill,
    ContextMenu,
    CopyPaste,
    CustomBorders,
    Formulas,
    ManualColumnMove,
    ManualColumnResize,
    ManualRowResize,
    ManualRowMove,
    MultipleSelectionHandles,
    registerPlugin,
    UndoRedo,
} from 'handsontable/plugins';

// Custom Plugins/Editors
import MilanoteFormulaCellReferencePlugin from './modules/MilanoteFormulaCellReferencePlugin';
import MilanoteFormulaAutocompletePlugin from './modules/MilanoteFormulaAutocompletePlugin';
import MilanoteEditingPlugin from './modules/MilanoteEditingPlugin';
import MilanoteEditingCleanupPlugin from './modules/MilanoteEditingCleanupPlugin';
import MilanoteContextMenuPlugin, { MILANOTE_CONTEXT_MENU_PLUGIN_NAME } from './modules/MilanoteContextMenuPlugin';

import MilanoteCellEditor from './modules/MilanoteCellEditor';
import MilanoteCellEditorComponent from './modules/MilanoteCellEditorComponent';

import hyperFormulaInstance from './manager/hyperFormulaInstance';

// Config
import getClientConfig from '../../utils/getClientConfig';

// Components
import TableResizeHandlers from './handlers/TableResizeHandlers';
import TableCellEditingHandlers from './handlers/TableCellEditingHandlers';
import TableCellSelectionHandlers from './handlers/TableCellSelectionHandlers';
import TableColumnResizeHandlers from './handlers/TableColumnResizeHandlers';
import TableMoveColumnRowHandlers from './handlers/TableMoveColumnRowHandlers';
import TableScrollHandlers from './handlers/TableScrollHandlers';
import TableContextMenuHandlers from './handlers/TableContextMenuHandlers';
import TableOperations from './handlers/TableOperations';
import TablePopups from './TablePopups';
import HotTableManager from './manager/HotTableManager';
import HotTableRendererPortalManager from './manager/HotTableRendererPortalManager';

// Utils
import { markEventAsHandled } from '../../utils/react/reactEventUtil';
import { getElementId } from '../../../common/elements/utils/elementPropertyUtils';
import { hasChanged } from '../../utils/react/propsComparisons';
import { getValueArray } from './utils/tableDataUtils';
import { convertColWidthsGUtoPx, getColWidthsResizeWidth } from './utils/tableSizeUtils';

// Selectors
import {
    getTableContentColWidthsGUSelector,
    getTableContentDataSelector,
    isCurrentlyEditingTableCell,
} from './tableSelector';
import { getThemeIsDarkMode } from '../../user/account/accountModal/interface/themeSettings/themeSelector';
import { getCurrentlyEditingEditorIdForElement } from '../selection/currentlyEditingSelector';
import { getIsRedoing, getIsUndoing } from '../../utils/undoRedo/undoRedoSelector';

// Types
import { TableAxis, TableOperation } from '../../../common/table/TableTypes';

// Styles
import './HotTable.scss';
import './HotTableRefHeaders.scss';
import './HotContextMenu.scss';

const config = getClientConfig();

// Register Handonstable plugins
registerPlugin(ContextMenu);
registerPlugin(AutoColumnSize);
registerPlugin(ManualColumnResize);
registerPlugin(ManualRowResize);
registerPlugin(ManualColumnMove);
registerPlugin(ManualRowMove);
registerPlugin(Formulas);
registerPlugin(CustomBorders);
registerPlugin(CopyPaste);
registerPlugin(UndoRedo);
registerPlugin(Autofill);
registerPlugin(MultipleSelectionHandles);

// Register Hansontable custom plugins
registerPlugin(MilanoteFormulaCellReferencePlugin);
registerPlugin(MilanoteFormulaAutocompletePlugin);
registerPlugin(MilanoteEditingPlugin);
registerPlugin(MilanoteEditingCleanupPlugin);
registerPlugin(MilanoteContextMenuPlugin);

// Register Hansontable custom editor
registerEditor('milanoteCellEditor', MilanoteCellEditor);

const isSingleSelectedHasChanged = hasChanged('isSingleSelected');

const mapStateToProps = (state, { elementId }) =>
    createStructuredSelector({
        tableContentData: getTableContentDataSelector(),
        tableContentColWidthsGU: getTableContentColWidthsGUSelector(),
        currentlyEditingEditorId: getCurrentlyEditingEditorIdForElement(elementId),
        isCurrentlyEditingTableCell: isCurrentlyEditingTableCell(elementId),
        isDarkMode: getThemeIsDarkMode,
        isUndoingOrRedoing: (state) => getIsUndoing(state) || getIsRedoing(state),
    });

class HotTable extends React.Component {
    constructor(props) {
        super(props);

        this.hotTableInstanceRef = props.hotTableInstanceRef;

        this.cellEditorRef = React.createRef();
        this.dropdownRef = React.createRef();

        this.tableCellEditingHandlersRef = React.createRef();
        this.tableCellSelectionHandlersRef = React.createRef();
        this.tableColumnResizeHandlersRef = React.createRef();
        this.tableMoveColumnRowHandlersRef = React.createRef();
        this.tableContextMenuHandlersRef = React.createRef();
        this.tableResizeHandlersRef = React.createRef();
        this.tableOperationsRef = React.createRef();
        this.hotTableRendererPortalManagerRef = React.createRef();

        this.state = {
            mounted: false,
            isSelectingCell: false,
            shouldCoverTable: !props.isSingleSelected,
        };
    }

    componentDidMount() {
        const {
            element,
            elementId,
            tableContentData,
            tableContentColWidthsGU,
            hotTableContainerRef,
            hotTableInstanceRef,
            gridSize,
            isDarkMode,
            inList,
            getContextZoomScale,
            showTitle,
            showCaption,
            dispatchStartEditingTableGrid,
        } = this.props;

        HotTableManager.addHotTableComponent(elementId, this);

        const tableContainer = hotTableContainerRef.current;

        const resizeToWidth = getColWidthsResizeWidth(element, inList, hotTableContainerRef, getContextZoomScale);
        const tableContentColWidthsPx = convertColWidthsGUtoPx(tableContentColWidthsGU, gridSize, resizeToWidth);

        hotTableInstanceRef.current = new Handsontable(tableContainer, {
            licenseKey: config.handsontable?.licenseKey,
            data: getValueArray(tableContentData),

            editor: 'milanoteCellEditor',
            renderer: this.hotTableRendererPortalManagerRef.current.getRendererWrapper(),

            colHeaders: true,
            rowHeaders: true,
            autoColumnSize: false,
            autoRowSize: false,
            manualRowMove: true,
            manualColumnMove: true,
            columnHeaderHeight: 0,
            rowHeaderHeight: 0,
            height: 'auto',
            formulas: { engine: hyperFormulaInstance, sheetName: elementId },
            manualColumnResize: tableContentColWidthsPx,
            manualRowResize: true,
            contextMenu: this.tableContextMenuHandlersRef.current.getTableContextMenuConfig(),
            outsideClickDeselects: false,
            colWidths: this.tableColumnResizeHandlersRef.current.getDefaultColWidth,

            // NOTE: Needs to be in the function format to avoid the `this` context being lost
            afterInit() {
                // This is a hack to get the table to get handsontable context menu event only triggered when there are
                // cell selections
                this.getPlugin(MILANOTE_CONTEXT_MENU_PLUGIN_NAME).removeHotContextMenuEventListeners();
            },

            afterViewRender: (...args) => this.hotTableRendererPortalManagerRef.current.afterViewRender(...args),

            beforeChange: (...args) => this.tableCellEditingHandlersRef.current.beforeChange(...args),
            // Wrap in setTimeout to make sure that the changes are applied to redux store first
            afterChange: (...args) => setTimeout(() => this.tableCellEditingHandlersRef.current?.afterChange(...args)),
            afterCreateRow: (index, amount, source) =>
                this.tableCellEditingHandlersRef.current.afterGridAltered(
                    TableAxis.ROW,
                    TableOperation.INSERT,
                    index,
                    amount,
                    source,
                ),
            afterCreateCol: (index, amount, source) =>
                this.tableCellEditingHandlersRef.current.afterGridAltered(
                    TableAxis.COL,
                    TableOperation.INSERT,
                    index,
                    amount,
                    source,
                ),
            afterRemoveRow: (index, amount, _, source) =>
                this.tableCellEditingHandlersRef.current.afterGridAltered(
                    TableAxis.ROW,
                    TableOperation.REMOVE,
                    index,
                    amount,
                    source,
                ),
            afterRemoveCol: (index, amount, _, source) =>
                this.tableCellEditingHandlersRef.current.afterGridAltered(
                    TableAxis.COL,
                    TableOperation.REMOVE,
                    index,
                    amount,
                    source,
                ),
            afterBeginEditing: (...args) => this.tableCellEditingHandlersRef.current.afterBeginEditing(...args),

            beforeAutofill: (...args) => this.tableCellEditingHandlersRef.current.beforeAutofill(...args),
            beforeCopy: (...args) => this.tableCellEditingHandlersRef.current.beforeCopy(...args),
            beforeCut: (...args) => this.tableCellEditingHandlersRef.current.beforeCut(...args),
            beforePaste: (...args) => this.tableCellEditingHandlersRef.current.beforePaste(...args),
            beforeOnCellMouseDown: (...args) => {
                this.tableCellEditingHandlersRef.current.beforeOnCellMouseDown(...args);
                this.tableMoveColumnRowHandlersRef.current.beforeOnCellMouseDown(...args);
            },

            afterScrollHorizontally: () => this.tableCellSelectionHandlersRef.current.afterScrollHorizontally(),

            beforeOnCellContextMenu: (...args) =>
                this.tableCellEditingHandlersRef.current.beforeOnCellContextMenu(...args),

            afterRowMove: (...args) =>
                this.tableCellEditingHandlersRef.current.afterGridMovement(TableAxis.ROW, ...args),
            afterColumnMove: (...args) =>
                this.tableCellEditingHandlersRef.current.afterGridMovement(TableAxis.COL, ...args),

            afterSelection: (...args) => {
                if (!this.state.isSelectingCell) requestAnimationFrame(() => this.setState({ isSelectingCell: true }));
                this.tableCellSelectionHandlersRef.current.afterSelection(...args);
            },
            afterSelectionEnd: (...args) => {
                if (this.state.isSelectingCell) requestAnimationFrame(() => this.setState({ isSelectingCell: false }));

                dispatchStartEditingTableGrid(elementId);
            },

            beforeColumnResize: (...args) => this.tableColumnResizeHandlersRef.current.beforeColumnResize(...args),
            afterColumnResize: (...args) => this.tableColumnResizeHandlersRef.current.afterColumnResize(...args),

            milanoteEditing: {
                tableOperationsRef: this.tableOperationsRef,
            },
            milanoteFormulaCellReference: {
                cellEditorRef: this.cellEditorRef,
            },
            milanoteFormulaAutocomplete: {
                enabled: true,
                cellEditorRef: this.cellEditorRef,
                dropdownRef: this.dropdownRef,
            },
        });

        hotTableInstanceRef.current.milanoteProps = {
            gridSize,
            isDarkMode,
            showTitle,
            showCaption,
            elementId: getElementId(element),
        };

        this.setState({ mounted: true });
    }

    componentDidUpdate(prevProps) {
        if (isSingleSelectedHasChanged(prevProps, this.props)) {
            // When isSingleSelected prop has changed, wait for a bit before covering table with overlay, this is to
            // allow all the necessary functions to complete before the table is covered
            if (this.props.isSingleSelected) {
                setTimeout(() => {
                    this.setState({ shouldCoverTable: false });
                }, 0);
            } else {
                this.setState({ shouldCoverTable: true });
            }
        }
    }

    componentWillUnmount() {
        const { element, hotTableInstanceRef } = this.props;
        HotTableManager.removeHotTableComponent(getElementId(element), this);

        // Destroy the hot table hooks
        Handsontable.hooks.destroy(hotTableInstanceRef.current);
    }

    onMouseDown = (event) => {
        // This is done in order to stop the parent element (in ElementContainer) to handle this event when other areas of
        // the table is clicked.

        const { isSingleSelected } = this.props;
        isSingleSelected && markEventAsHandled(event);
    };

    render() {
        const { gridSize, hotTableContainerRef, hotTableInstanceRef } = this.props;
        const { mounted, isSelectingCell } = this.state;

        return (
            <div
                className={classNames('HotTable', { 'is-selecting-cell': isSelectingCell })}
                onMouseDown={this.onMouseDown}
            >
                <TableScrollHandlers
                    tableOperationsRef={this.tableOperationsRef}
                    shouldCoverTable={this.state.shouldCoverTable}
                    {...this.props}
                />

                <TableOperations ref={this.tableOperationsRef} {...this.props} />

                <TableCellEditingHandlers
                    ref={this.tableCellEditingHandlersRef}
                    tableOperationsRef={this.tableOperationsRef}
                    {...this.props}
                />
                <TableCellSelectionHandlers
                    ref={this.tableCellSelectionHandlersRef}
                    tableOperationsRef={this.tableOperationsRef}
                    {...this.props}
                />
                <TableColumnResizeHandlers
                    ref={this.tableColumnResizeHandlersRef}
                    tableOperationsRef={this.tableOperationsRef}
                    {...this.props}
                />
                <TableResizeHandlers
                    ref={this.tableResizeHandlersRef}
                    tableOperationsRef={this.tableOperationsRef}
                    {...this.props}
                />
                <TableMoveColumnRowHandlers
                    ref={this.tableMoveColumnRowHandlersRef}
                    tableOperationsRef={this.tableOperationsRef}
                    {...this.props}
                />
                <TableContextMenuHandlers
                    ref={this.tableContextMenuHandlersRef}
                    tableOperationsRef={this.tableOperationsRef}
                    {...this.props}
                />

                <MilanoteCellEditorComponent
                    {...this.props}
                    cellEditorRef={this.cellEditorRef}
                    hotTableContainerRef={hotTableContainerRef}
                    hotTableInstanceRef={hotTableInstanceRef}
                    gridSize={gridSize}
                    // Pass in mounted prop to ensure that the MilanoteCellEditorComponent
                    // is re-rendered after HotTable is mounted
                    mounted={mounted}
                />
                <TablePopups
                    {...this.props}
                    isEditing={this.state.isEditing}
                    dropdownRef={this.dropdownRef}
                    cellEditorRef={this.cellEditorRef}
                />

                <HotTableRendererPortalManager ref={this.hotTableRendererPortalManagerRef} />

                <div className="HotTableLib" ref={hotTableContainerRef}></div>
            </div>
        );
    }
}

HotTable.propTypes = {
    element: PropTypes.object,
    elementId: PropTypes.string,
    tableContentData: PropTypes.array,
    tableContentColWidthsGU: PropTypes.array,
    dispatchUpdateTableElement: PropTypes.func,
    gridSize: PropTypes.number,
    tempSize: PropTypes.object,
    isPresentational: PropTypes.bool,
    isSingleSelected: PropTypes.bool,
    isResizing: PropTypes.bool,
    isDarkMode: PropTypes.bool,
    inList: PropTypes.string,
    showTitle: PropTypes.bool,
    showCaption: PropTypes.bool,
    isCurrentlyEditingTableCell: PropTypes.bool,

    dispatchSetCellSelection: PropTypes.func,

    dispatchUndoAction: PropTypes.func,
    dispatchRedoAction: PropTypes.func,
    dispatchDeselectElement: PropTypes.func,
    dispatchStartEditingTableGrid: PropTypes.func,

    hotTableInstanceRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    hotTableContainerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    getContextZoomScale: PropTypes.func,
};

export default connect(mapStateToProps)(HotTable);
